import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, HeartPulse, RefreshCw, Tags } from "lucide-react";
import { FrontendErrorAlertsPanel } from "@/components/admin/FrontendErrorAlertsPanel";
import { FrontendErrorDrilldownPanel } from "@/components/admin/FrontendErrorDrilldownPanel";
import { FrontendErrorRecipientsPanel } from "@/components/admin/FrontendErrorRecipientsPanel";
import { FrontendErrorIncidentsPanel } from "@/components/admin/FrontendErrorIncidentsPanel";
import { FrontendErrorRegressionRoutingPanel } from "@/components/admin/FrontendErrorRegressionRoutingPanel";
import { RuntimeHealthAuditPanel } from "@/components/admin/RuntimeHealthAuditPanel";
import { TrendSanitizeEventsPanel } from "@/components/admin/TrendSanitizeEventsPanel";
import { TrendSanitizeAlertSettingsPanel } from "@/components/admin/TrendSanitizeAlertSettingsPanel";

import type { DrilldownFilters } from "@/lib/runtimeHealthBus";

type Regression = {
  release: string;
  baseline_release: string | null;
  release_rate: number;
  baseline_rate: number;
  z_score: number | null;
  p_value: number | null;
  significant: boolean;
  severity: string;
  detected_at: string;
};

type ErrorRow = {
  id: string;
  created_at: string;
  error_type: string;
  message: string;
  route: string;
  source: string | null;
  lineno: number | null;
  colno: number | null;
  stack: string | null;
  release: string | null;
  build_commit: string | null;
  fingerprint: string | null;
  session_id: string | null;
};


const WINDOWS = [
  { id: "24h", label: "24 hours", hours: 24 },
  { id: "7d", label: "7 days", hours: 24 * 7 },
  { id: "30d", label: "30 days", hours: 24 * 30 },
] as const;

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function RuntimeHealthPanel() {
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [windowId, setWindowId] = useState<(typeof WINDOWS)[number]["id"]>("24h");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [regressions, setRegressions] = useState<Record<string, Regression>>({});
  const [detecting, setDetecting] = useState(false);
  const [drilldownFilters, setDrilldownFilters] = useState<DrilldownFilters>({});

  const loadRegressions = useCallback(async () => {
    const { data } = await supabase
      .from("frontend_error_release_regressions")
      .select("release, baseline_release, release_rate, baseline_rate, z_score, p_value, significant, severity, detected_at")
      .order("detected_at", { ascending: false });
    const map: Record<string, Regression> = {};
    for (const row of (data ?? []) as unknown as Regression[]) {
      if (!map[row.release]) map[row.release] = row;
    }
    setRegressions(map);
  }, []);

  const runDetection = useCallback(async () => {
    setDetecting(true);
    const hours = WINDOWS.find((w) => w.id === windowId)!.hours;
    await supabase.rpc("detect_release_regressions", { _window_hours: hours });
    await loadRegressions();
    setDetecting(false);
  }, [loadRegressions, windowId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const hours = WINDOWS.find((w) => w.id === windowId)!.hours;
    const since = new Date(Date.now() - hours * 3600_000).toISOString();
    const { data, error: err } = await supabase
      .from("frontend_error_events")
      .select("id, created_at, error_type, message, route, source, lineno, colno, stack, release, build_commit, fingerprint, session_id")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(500);
    if (err) setError(err.message);
    else setRows((data ?? []) as ErrorRow[]);
    setLoading(false);
  }, [windowId]);

  useEffect(() => {
    void load();
    void loadRegressions();
  }, [load, loadRegressions]);

  const byRoute = useMemo(() => {
    const map = new Map<string, { route: string; count: number; last: string; sessions: Set<string> }>();
    for (const r of rows) {
      const entry = map.get(r.route) ?? { route: r.route, count: 0, last: r.created_at, sessions: new Set<string>() };
      entry.count += 1;
      if (new Date(r.created_at) > new Date(entry.last)) entry.last = r.created_at;
      if (r.session_id) entry.sessions.add(r.session_id);
      map.set(r.route, entry);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [rows]);

  // Crash rate per release/build so regressions between deployments stand out.
  const byRelease = useMemo(() => {
    const map = new Map<
      string,
      { release: string; commit: string | null; count: number; sessions: Set<string>; last: string }
    >();
    for (const r of rows) {
      const key = r.release ?? "unknown";
      const entry =
        map.get(key) ?? { release: key, commit: r.build_commit, count: 0, sessions: new Set<string>(), last: r.created_at };
      entry.count += 1;
      if (r.session_id) entry.sessions.add(r.session_id);
      if (new Date(r.created_at) > new Date(entry.last)) entry.last = r.created_at;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [rows]);

  const byFingerprint = useMemo(() => {
    const map = new Map<string, { fingerprint: string; message: string; count: number; route: string }>();
    for (const r of rows) {
      const key = r.fingerprint ?? "unfingerprinted";
      const entry = map.get(key) ?? { fingerprint: key, message: r.message, count: 0, route: r.route };
      entry.count += 1;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.count - a.count).slice(0, 10);
  }, [rows]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <HeartPulse className="size-4 text-primary" /> Runtime health
            </CardTitle>
            <CardDescription>
              Uncaught frontend errors reported by real browsers, grouped by page.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {WINDOWS.map((w) => (
              <Button
                key={w.id}
                size="sm"
                variant={windowId === w.id ? "default" : "outline"}
                onClick={() => setWindowId(w.id)}
              >
                {w.label}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertTriangle className="size-4" /> {error}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Errors in window" value={String(rows.length)} />
            <Stat label="Affected pages" value={String(byRoute.length)} />
            <Stat
              label="Last error"
              value={rows[0] ? timeAgo(rows[0].created_at) : "none"}
            />
          </div>

          {byRoute.length === 0 && !loading && !error ? (
            <p className="text-sm text-muted-foreground">
              No frontend errors reported in this window. 🎉
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Page</th>
                    <th className="py-2 pr-4 font-medium">Errors</th>
                    <th className="py-2 pr-4 font-medium">Sessions</th>
                    <th className="py-2 font-medium">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {byRoute.map((r) => (
                    <tr key={r.route} className="border-b border-border/50 last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{r.route}</td>
                      <td className="py-2 pr-4">{r.count}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{r.sessions.size}</td>
                      <td className="py-2 text-muted-foreground">
                        {timeAgo(r.last)}{" "}
                        <span className="font-mono text-xs">
                          ({new Date(r.last).toLocaleString()})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tags className="size-4 text-primary" /> Crashes by release
          </CardTitle>
          <CardDescription>
            Compare crash volume between deployments. A release is flagged when its errors-per-session
            beats the baseline release with a two-proportion z-test at p ≤ 0.05.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void runDetection()} disabled={detecting}>
              <RefreshCw className={`size-3.5 ${detecting ? "animate-spin" : ""}`} />
              {detecting ? "Analysing…" : "Run regression detection"}
            </Button>
            {Object.values(regressions).some((r) => r.significant) && (
              <span className="text-xs text-destructive">
                Significant crash-rate spike detected — see the flagged rows below.
              </span>
            )}
          </div>
          {byRelease.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tagged errors in this window.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Release</th>
                    <th className="py-2 pr-4 font-medium">Commit</th>
                    <th className="py-2 pr-4 font-medium">Errors</th>
                    <th className="py-2 pr-4 font-medium">Sessions</th>
                    <th className="py-2 pr-4 font-medium">Regression</th>
                    <th className="py-2 font-medium">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {byRelease.map((r) => (
                    <tr key={r.release} className="border-b border-border/50 last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{r.release}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">
                        {r.commit ? r.commit.slice(0, 7) : "—"}
                      </td>
                      <td className="py-2 pr-4">{r.count}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{r.sessions.size}</td>
                      <td className="py-2 pr-4">
                        <ReleaseRegressionCell regression={regressions[r.release]} />
                      </td>
                      <td className="py-2 text-muted-foreground">{timeAgo(r.last)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {byFingerprint.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Top deduped crashes</h4>
              {byFingerprint.map((f) => (
                <div key={f.fingerprint} className="rounded-md border border-border p-2 text-xs">
                  <span className="font-mono">{f.fingerprint}</span> · {f.count}× ·{" "}
                  <span className="font-mono text-muted-foreground">{f.route}</span>
                  <p className="mt-1 break-words">{f.message}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <FrontendErrorAlertsPanel />

      <FrontendErrorRecipientsPanel />

      <FrontendErrorRegressionRoutingPanel />

      <FrontendErrorIncidentsPanel prefill={drilldownFilters} />

      <TrendSanitizeAlertSettingsPanel />

      <TrendSanitizeEventsPanel />

      <RuntimeHealthAuditPanel />

      <FrontendErrorDrilldownPanel onFiltersChange={setDrilldownFilters} />

      <Card>
        <CardHeader>
          <CardTitle>Recent errors</CardTitle>
          <CardDescription>
            Newest first. Production bundles ship source maps, so file/line resolves to original source.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing to show.</p>}
          {rows.slice(0, 50).map((r) => (
            <div key={r.id} className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="font-mono">{r.error_type}</Badge>
                <span className="font-mono">{r.route}</span>
                <span>{new Date(r.created_at).toLocaleString()}</span>
                {r.release && <span className="font-mono">rel:{r.release}</span>}
              </div>
              <p className="mt-2 break-words text-sm">{r.message}</p>
              {r.source && (
                <p className="mt-1 font-mono text-xs text-muted-foreground break-all">
                  {r.source}
                  {r.lineno != null ? `:${r.lineno}` : ""}
                  {r.colno != null ? `:${r.colno}` : ""}
                </p>
              )}
              {r.stack && (
                <>
                  <button
                    className="mt-2 text-xs text-primary underline"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    {expanded === r.id ? "Hide stack" : "Show stack"}
                  </button>
                  {expanded === r.id && (
                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[11px] whitespace-pre-wrap">
                      {r.stack}
                    </pre>
                  )}
                </>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ReleaseRegressionCell({ regression }: { regression?: Regression }) {
  if (!regression || regression.z_score == null) {
    return <span className="text-xs text-muted-foreground">not enough data</span>;
  }
  const delta =
    regression.baseline_rate > 0
      ? `${(((regression.release_rate - regression.baseline_rate) / regression.baseline_rate) * 100).toFixed(0)}%`
      : "new";
  return (
    <span className="flex flex-wrap items-center gap-1 text-xs">
      <Badge variant={regression.significant ? "destructive" : "secondary"}>
        {regression.significant ? "regression" : "stable"}
      </Badge>
      <span className="font-mono text-muted-foreground">
        {regression.release_rate}/session vs {regression.baseline_rate} ({delta}) · z=
        {regression.z_score} · p={regression.p_value ?? "—"}
      </span>
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-bold">{value}</div>
    </div>
  );
}

export default RuntimeHealthPanel;
